import { setSafeHtml } from '../utils/dom';
import AbstractBoxedWidget from './AbstractBoxedWidget';
import { fetchMetadata, makeDisplayValue } from './widgetDataUtils';

export default class MetadataWidget extends AbstractBoxedWidget {
    render = async (data: any, options: { [id: string]: any } = {}) => {
        if (
            this.container !== undefined &&
            this.container !== null &&
            this.design !== undefined &&
            this.design !== null
        ) {
            // Basics - this will give us the box or the icon content area... using super class
            const settings: { [id: string]: any } = {
                    ...AbstractBoxedWidget.getDefaults(),
                    ...this.design
                },
                tableContentElement = this.renderBox(data, {
                    ...settings,
                    element: 'div',
                    boxClass: 'rb-metadata-box',
                    messageBackground: settings.backgroundColor,
                    messageContainerClass: 'rb-metadata-container',
                    showTitle: true
                }),
                doc = this.container.ownerDocument,
                topWidget = this.container.closest<HTMLElement>('.rb-report-widget'),
                {
                    indicators = [],
                    displayFields = 'Title,Description',
                    serviceUrl = 'https://hub.instantatlas.com/data-catalog-metadata-service',
                    layout = 'table',
                    numberFormat,
                    skipEmptyValues = true,
                    dateTimeFormat = 'ISODATE'
                } = settings;
            const indicatorIds = indicators
                .map((i: any) => i.id)
                .filter((iid: any) => iid !== undefined && iid !== null && typeof iid === 'string');
            if (indicatorIds.length > 0) {
                try {
                    const metadataRsp = await fetchMetadata(indicatorIds, displayFields, serviceUrl);
                    const { features, fields } = metadataRsp ?? {};
                    if (
                        features !== undefined &&
                        Array.isArray(features) &&
                        fields !== undefined &&
                        Array.isArray(fields)
                    ) {
                        const metaContainer =
                            layout === 'table'
                                ? doc.createElement('tbody')
                                : layout === 'text'
                                ? doc.createElement('div')
                                : doc.createElement('dl');
                        metaContainer.setAttribute('class', `rb-metadata rb-metadata-${layout}`);
                        if (layout === 'table') {
                            for (const entry of features) {
                                for (const fld of fields) {
                                    const v = entry.attributes[fld.name];
                                    if ((v === undefined || v === null) && skipEmptyValues) {
                                        continue;
                                    }
                                    const dv = makeDisplayValue(v, fld.type, numberFormat, dateTimeFormat);
                                    const ttl = doc.createElement('th');
                                    ttl.appendChild(doc.createTextNode(fld.alias ?? fld.name));
                                    const cell = doc.createElement('td');
                                    if (dv.html) {
                                        setSafeHtml(cell, dv.text);
                                    } else {
                                        cell.appendChild(doc.createTextNode(dv.text));
                                    }
                                    const row = doc.createElement('tr');
                                    row.appendChild(ttl);
                                    row.appendChild(cell);
                                    metaContainer.appendChild(row);
                                }
                            }
                            const tbl = doc.createElement('table');
                            const thd = doc.createElement('thead');
                            const topRow = doc.createElement('tr');
                            thd.appendChild(topRow);
                            tbl.appendChild(thd);
                            tbl.appendChild(metaContainer);
                            tableContentElement?.appendChild(tbl);
                        } else if (layout === 'text') {
                            const txt: string[] = [];
                            let isHtml = false;
                            for (const entry of features) {
                                for (const fld of fields) {
                                    const v = entry.attributes[fld.name];
                                    if ((v === undefined || v === null) && skipEmptyValues) {
                                        continue;
                                    }
                                    const dv = makeDisplayValue(v, fld.type, numberFormat, dateTimeFormat);
                                    txt.push(dv.text);
                                    isHtml = isHtml || dv.html;
                                }
                            }
                            if (isHtml) {
                                setSafeHtml(metaContainer, txt.join(' '));
                            } else {
                                metaContainer.appendChild(doc.createTextNode(txt.join(' ')));
                            }
                            tableContentElement?.appendChild(metaContainer);
                        } else {
                            for (const entry of features) {
                                for (const fld of fields) {
                                    const v = entry.attributes[fld.name];
                                    if ((v === undefined || v === null) && skipEmptyValues) {
                                        continue;
                                    }
                                    const dv = makeDisplayValue(v, fld.type, numberFormat, dateTimeFormat);
                                    const ttl = doc.createElement('dt');
                                    ttl.appendChild(doc.createTextNode(fld.alias ?? fld.name));
                                    const cell = doc.createElement('dd');
                                    if (dv.html) {
                                        setSafeHtml(cell, dv.text);
                                    } else {
                                        cell.appendChild(doc.createTextNode(dv.text));
                                    }
                                    metaContainer.appendChild(ttl);
                                    metaContainer.appendChild(cell);
                                }
                            }
                            tableContentElement?.appendChild(metaContainer);
                        }
                    }
                } catch (metadataEx) {
                    this.renderError(metadataEx, this.design.id, 'Metadata');
                }
            }
            if (topWidget !== null) {
                topWidget.setAttribute(
                    'class',
                    (topWidget.getAttribute('class') ?? '').replace(/\s(placeholder)/g, '')
                );
            }
        }
    };

    static getDefaults = () => {
        return {
            ...AbstractBoxedWidget.getDefaults(),
            serviceUrl: 'https://hub.instantatlas.com/data-catalog-metadata-service',
            layout: 'table', // 'table' or 'list' or 'text',
            messagePadding: '0',
            dateTimeFormat: 'ISODATE'
        };
    };
}

/*
OBJECTID (type: esriFieldTypeOID, alias: OBJECTID, SQL Type: sqlTypeOther, length: 0, nullable: false, editable: false)
ThemeNameTrail (type: esriFieldTypeString, alias: ThemeNameTrail, SQL Type: sqlTypeOther, length: 500, nullable: true, editable: true)
ThemeIdTrail (type: esriFieldTypeString, alias: ThemeIdTrail, SQL Type: sqlTypeOther, length: 500, nullable: true, editable: true)
Title (type: esriFieldTypeString, alias: Title, SQL Type: sqlTypeOther, length: 255, nullable: true, editable: true)
IndicatorID (type: esriFieldTypeString, alias: IndicatorID, SQL Type: sqlTypeOther, length: 255, nullable: true, editable: true)
Description (type: esriFieldTypeString, alias: Description, SQL Type: sqlTypeOther, length: 4000, nullable: true, editable: true)
Subject (type: esriFieldTypeString, alias: Subject, SQL Type: sqlTypeOther, length: 200, nullable: true, editable: true)
Source (type: esriFieldTypeString, alias: Source, SQL Type: sqlTypeOther, length: 2000, nullable: true, editable: true)
Issued (type: esriFieldTypeDate, alias: Issued, SQL Type: sqlTypeOther, length: 8, nullable: true, editable: true)
LastPublished (type: esriFieldTypeDate, alias: LastPublished, SQL Type: sqlTypeOther, length: 8, nullable: true, editable: true)
Spatial (type: esriFieldTypeString, alias: Spatial, SQL Type: sqlTypeOther, length: 1000, nullable: true, editable: true)
Temporal (type: esriFieldTypeString, alias: Temporal, SQL Type: sqlTypeOther, length: 75, nullable: true, editable: true)
Rights (type: esriFieldTypeString, alias: Rights, SQL Type: sqlTypeOther, length: 1000, nullable: true, editable: true)
Keywords (type: esriFieldTypeString, alias: Keywords, SQL Type: sqlTypeOther, length: 1000, nullable: true, editable: true)
Status (type: esriFieldTypeString, alias: Status, SQL Type: sqlTypeOther, length: 50, nullable: true, editable: true)
LastRevised (type: esriFieldTypeDate, alias: LastRevised, SQL Type: sqlTypeOther, length: 8, nullable: true, editable: true)
LastUpdated (type: esriFieldTypeDate, alias: LastUpdated, SQL Type: sqlTypeOther, length: 8, nullable: true, editable: true)
Creator (type: esriFieldTypeString, alias: Creator, SQL Type: sqlTypeOther, length: 300, nullable: true, editable: true)
Publisher (type: esriFieldTypeString, alias: Publisher, SQL Type: sqlTypeOther, length: 100, nullable: true, editable: true)
UpdateFrequency (type: esriFieldTypeString, alias: UpdateFrequency, SQL Type: sqlTypeOther, length: 300, nullable: true, editable: true)
Methodology (type: esriFieldTypeString, alias: Methodology, SQL Type: sqlTypeOther, length: 2000, nullable: true, editable: true)
InstanceID (type: esriFieldTypeString, alias: InstanceID, SQL Type: sqlTypeOther, length: 255, nullable: true, editable: true)
GeoID (type: esriFieldTypeString, alias: GeoID, SQL Type: sqlTypeOther, length: 255, nullable: true, editable: true)
*/
